/**
 * Utilitaires pour la validation et manipulation des coordonnées géographiques
 */

/**
 * Valide si des coordonnées géographiques sont valides
 * @param latitude Latitude (-90 à 90)
 * @param longitude Longitude (-180 à 180)
 * @returns true si les coordonnées sont valides, false sinon
 */
export function validateCoordinates(
  latitude: number | null | undefined,
  longitude: number | null | undefined
): boolean {
  // Vérifier que les valeurs existent
  if (latitude === null || latitude === undefined) return false;
  if (longitude === null || longitude === undefined) return false;

  // Vérifier que ce sont des nombres
  if (typeof latitude !== "number" || typeof longitude !== "number") return false;

  // Vérifier que ce ne sont pas NaN ou Infinity
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;

  // Vérifier les plages valides
  if (latitude < -90 || latitude > 90) return false;
  if (longitude < -180 || longitude > 180) return false;

  return true;
}

/**
 * Valide et normalise des coordonnées
 * @param latitude Latitude
 * @param longitude Longitude
 * @returns Coordonnées normalisées ou null si invalides
 */
export function normalizeCoordinates(
  latitude: number | null | undefined,
  longitude: number | null | undefined
): { latitude: number; longitude: number } | null {
  if (!validateCoordinates(latitude, longitude)) {
    return null;
  }

  return {
    latitude: latitude as number,
    longitude: longitude as number,
  };
}

/**
 * Génère une URL de navigation pour différentes plateformes
 * @param latitude Latitude
 * @param longitude Longitude
 * @param platform Plateforme cible ('ios' | 'android' | 'web')
 * @returns URL de navigation
 */
export function getNavigationUrl(
  latitude: number,
  longitude: number,
  platform: "ios" | "android" | "web" = "web"
): string {
  if (!validateCoordinates(latitude, longitude)) {
    throw new Error("Coordonnées invalides");
  }

  switch (platform) {
    case "ios":
      return `maps://maps.apple.com/?daddr=${latitude},${longitude}`;
    case "android":
      return `google.navigation:q=${latitude},${longitude}`;
    case "web":
    default:
      return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
  }
}

/**
 * Détecte la plateforme de l'utilisateur
 * @returns Plateforme détectée
 */
export function detectPlatform(): "ios" | "android" | "web" {
  if (typeof window === "undefined") return "web";

  const userAgent = window.navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return "ios";
  }

  if (/android/.test(userAgent)) {
    return "android";
  }

  return "web";
}

/**
 * Génère une URL de navigation adaptée à la plateforme de l'utilisateur
 * @param latitude Latitude
 * @param longitude Longitude
 * @returns URL de navigation
 */
export function getNavigationUrlForPlatform(latitude: number, longitude: number): string {
  const platform = detectPlatform();
  return getNavigationUrl(latitude, longitude, platform);
}
